
import { Component, Vue } from 'vue-property-decorator'
import { colors, RouteName } from '@/utils/constants'

import {
  AdviceUserRole,
  IActiveAdvice,
  PhaseGroup,
  Project,
} from '@/services/bimoboxApiService/types'

import { getAlertModule, getProjectModule } from '@/store/utils'

import { AdviceService } from '@/services/bimoboxApiService'
import { VuetifyDataTableHeader } from '@/types/global'

const Store = {
  ...getProjectModule(),
  ...getAlertModule(),
}

interface DisplayableActiveAdvice {
  documentId: string
  documentName: string
  requestedDate: string
  phaseId: string
  phaseName: string
  userRole: AdviceUserRole
}

@Component({ name: 'active-advice-table' })
export default class ActiveAdviceTable extends Vue {
  @Store.ProjectModule.State
  readonly project: Project

  @Store.AlertModule.Mutation
  readonly ALERT_ERROR: (message: string) => void

  readonly colors = colors
  readonly adviceService = new AdviceService()

  advices: IActiveAdvice[] = []
  loading = false

  get activeAdvices(): DisplayableActiveAdvice[] {
    return this.advices.map((v) => ({
      documentId: v.documentId,
      documentName: v.documentName,
      requestedDate: v.requestedAt,
      phaseId: v.phaseId,
      phaseName: this.formatPhaseName(v.phaseName, v.phaseGroup),
      userRole: v.userRole,
    }))
  }

  formatPhaseName(name: string, group: PhaseGroup): string {
    const grpStr = (this.$t(`phase_group_${group}`) as string)
      .slice(0, 4)
      .toUpperCase()

    return `${grpStr} / ${name}`
  }

  get headerInfo(): VuetifyDataTableHeader[] {
    return [
      {
        text: this.$t('active_advice_document_name') as string,
        value: 'documentName',
      },
      {
        text: this.$t('active_advice_role') as string,
        value: 'userRole',
        width: '80px',
        align: 'center',
      },
      {
        text: this.$t('active_advice_requested_date') as string,
        value: 'requestedDate',
        align: 'end',
      },
    ]
  }

  mounted(): void {
    this.fetchActiveAdvices()
  }

  getUserRoleTooltip(role: AdviceUserRole): string {
    switch (role) {
      case 'none':
        return this.$t('active_advice_role_tooltip_none') as string
      case 'signer':
        return this.$t('active_advice_role_tooltip_signer_not_signed') as string
      case 'requester':
        return this.$t('active_advice_role_tooltip_requester') as string
    }
  }

  getUserRoleColor(role: AdviceUserRole): string {
    switch (role) {
      case 'none':
        return 'grey'
      case 'signer':
        return 'warning'
      case 'requester':
        return 'info'
    }
  }

  getUserRoleIcon(role: AdviceUserRole): string {
    switch (role) {
      case 'none':
        return 'mdi-account-cancel'
      case 'signer':
        return 'mdi-account-edit'
      case 'requester':
        return 'mdi-account-group'
    }
  }

  selectAdvice(item: DisplayableActiveAdvice): void {
    this.$router.push({
      name: RouteName.DOCUMENTS,
      query: { document: item.documentId, phase: item.phaseId },
    })
  }

  fetchActiveAdvices(): void {
    this.loading = true

    this.adviceService
      .getActiveAdvices(this.project.id)
      .then((advices) => (this.advices = advices))
      .catch(() => this.ALERT_ERROR('active_advice_load_error'))
      .finally(() => (this.loading = false))
  }
}
